import {
  AddIcon,
  Alert,
  Button,
  Card,
  EmptyState,
  Stack,
  Tab,
  TabPanel,
  Tabs,
} from '@all-manager/design-system-v2';
import FullPageLayout from '../../../layout/FullPageLayout/FullPageLayout';
import AccountingSkeleton from '../components/AccountingSkeleton/AccountingSkeleton';
import CategoryComparison from '../components/CategoryComparison/CategoryComparison';
import CategoryDetails from '../components/CategoryDetails/CategoryDetails';
import ExpenseCategoryFormDialog from '../components/ExpenseCategoryFormDialog/ExpenseCategoryFormDialog';
import YearBreakdown from '../components/YearBreakdown/YearBreakdown';
import { AccountingTab, useAccountingPage } from './useAccountingPage';

const TABS: { value: AccountingTab; label: string }[] = [
  { value: 'breakdown', label: 'Répartition' },
  { value: 'category', label: 'Par catégorie' },
  { value: 'comparison', label: 'Comparaison' },
];

function tabId(value: AccountingTab) {
  return `accounting-tab-${value}`;
}

function panelId(value: AccountingTab) {
  return `accounting-panel-${value}`;
}

export default function AccountingPage() {
  const hook = useAccountingPage();

  return (
    <>
      <FullPageLayout
        htmlTitle="Comptabilité"
        title="Comptabilité"
        subtitle="Suivi des dépenses de la copropriété"
        action={hook.isAdmin && hook.categories.length > 0 ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={hook.handleOpenCategoryForm}
          >
            Nouvelle catégorie
          </Button>
        ) : undefined}
      >
        {hook.isError ? (
          <Alert
            severity="error"
            action={(
              <Button variant="text" size="small" color="inherit" onClick={hook.handleRetry}>
                Réessayer
              </Button>
            )}
          >
            Les dépenses n'ont pas pu être chargées. Vérifiez votre connexion puis réessayez.
          </Alert>
        ) : hook.isLoading ? (
          <AccountingSkeleton />
        ) : hook.categories.length === 0 ? (
          <Card>
            <EmptyState
              title="Aucune catégorie de dépenses"
              subtitle={hook.isAdmin
                ? 'Créez une catégorie (eau, électricité, ascenseur…) puis saisissez ses dépenses mois par mois.'
                : 'Les dépenses apparaîtront ici dès qu\'un administrateur aura créé une catégorie.'}
              action={hook.isAdmin ? (
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={hook.handleOpenCategoryForm}>
                  Créer une catégorie
                </Button>
              ) : undefined}
            />
          </Card>
        ) : (
          <Stack spacing={3}>
            <Tabs<AccountingTab>
              value={hook.tab}
              onChange={hook.handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              divider
              aria-label="Vues de la comptabilité"
            >
              {TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  id={tabId(tab.value)}
                  aria-controls={panelId(tab.value)}
                />
              ))}
            </Tabs>

            {TABS.map((tab) => (
              <TabPanel
                key={tab.value}
                value={tab.value}
                activeValue={hook.tab}
                id={panelId(tab.value)}
                aria-labelledby={tabId(tab.value)}
              >
                {tab.value === 'breakdown' ? (
                  <YearBreakdown categories={hook.categories} expenses={hook.expenses} />
                ) : tab.value === 'category' ? (
                  <CategoryDetails categories={hook.categories} expenses={hook.expenses} />
                ) : (
                  <CategoryComparison categories={hook.categories} expenses={hook.expenses} />
                )}
              </TabPanel>
            ))}
          </Stack>
        )}
      </FullPageLayout>

      {hook.isCategoryFormOpen ? (
        <ExpenseCategoryFormDialog onClose={hook.handleCloseCategoryForm} />
      ) : null}
    </>
  );
}
